import { lazy, Suspense, type ComponentType } from 'react';
import { Routes, Route } from 'react-router-dom';
import { LandingPage } from './pages/LandingPage';

/**
 * Lazily loads a page that is a named export. Everything but the landing is split out this
 * way, so the landing's bundle carries no auth, no app pages and no legal texts.
 */
function lazyNamed<K extends string, P extends object>(
  load: () => Promise<Record<K, ComponentType<P>>>,
  name: K,
) {
  return lazy(() => load().then((module) => ({ default: module[name] })));
}

const AppShell = lazy(() => import('./components/AppShell'));
const ProtectedRoute = lazyNamed(() => import('./components/ProtectedRoute'), 'ProtectedRoute');
const ProfilePage = lazyNamed(() => import('./pages/ProfilePage'), 'ProfilePage');
const LoginPage = lazyNamed(() => import('./pages/LoginPage'), 'LoginPage');
const DeleteAccountPage = lazyNamed(() => import('./pages/DeleteAccountPage'), 'DeleteAccountPage');
const ImpressumPage = lazyNamed(() => import('./pages/ImpressumPage'), 'ImpressumPage');
const PrivacyPage = lazyNamed(() => import('./pages/PrivacyPage'), 'PrivacyPage');
const TermsPage = lazyNamed(() => import('./pages/TermsPage'), 'TermsPage');
const SupportPage = lazyNamed(() => import('./pages/SupportPage'), 'SupportPage');

function App() {
  return (
    <Routes>
      {/* Marketing landing — standalone (its own nav/footer, no app chrome). */}
      <Route path="/" element={<LandingPage />} />

      {/* App pages share the authenticated app chrome (its <Outlet /> has its own Suspense). */}
      <Route
        element={
          <Suspense fallback={null}>
            <AppShell />
          </Suspense>
        }
      >
        <Route path="login" element={<LoginPage />} />
        {/* Public — Google Play requires a deletion URL reachable without the app. */}
        <Route path="delete-account" element={<DeleteAccountPage />} />

        {/* Public legal pages. Both stores require a Privacy Policy URL that
            resolves without a login, and § 5 DDG requires the imprint to be
            permanently available. German aliases are registered too, because
            that is what German users (and the store forms) tend to look for. */}
        <Route path="impressum" element={<ImpressumPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="datenschutz" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="agb" element={<TermsPage />} />
        {/* Public — the App Store listing's Support URL must resolve without a login. */}
        <Route path="support" element={<SupportPage />} />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
